import React, { useState } from 'react';
import { Table, Button as AntButton, Spin, Progress } from 'antd';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { SyncOutlined } from '@ant-design/icons';

const ObjectivesAdminCreationContent = ({
  dataSource,
  loading,
  totalPages,
  totalElements,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  fetchData,
  getCsvLogsPage
}) => {
  // COLUMNAS TABLA
  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      align: 'center'
    },
    {
      title: 'Distribuidor',
      dataIndex: 'distribuidor',
      key: 'distribuidor',
      align: 'center'
    },
    {
      title: 'Objetivo',
      dataIndex: 'objetivo',
      key: 'objetivo',
      align: 'center'
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      align: 'center'
    },
  ];

  // VARIABLES DE CONTROL
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isFetchDisabled, setIsFetchDisabled] = useState(false);

  // Función para descargar la tabla completa
  const downloadTable = async () => {
    try {
      setIsDownloading(true);
      setDownloadProgress(0);
      let allData = [];

      for (let page = 0; page < totalPages; page++) {
        const { data } = await getCsvLogsPage({
          variables: {
            page,
            size: pageSize,
            sortBy: "fecha",
            sortDir: "desc"
          },
          fetchPolicy: "no-cache"
        });

        const pageData = data.csvLogsPage.content.map(item => [
          dayjs(item.fecha).format('DD/MM/YYYY HH:mm'),
          item.codigoDistribuidor,
          item.nombreObjetivo,
          item.estado
        ]);

        allData = [...allData, ...pageData];
        setDownloadProgress(Math.round(((page + 1) / totalPages) * 100));
      }

      const csvContent = [
        ['Fecha', 'Distribuidor', 'Objetivo', 'Estado'],
        ...allData
      ]
        .map(e => e.join(';'))
        .join('\n');

      const bom = '\uFEFF';
      const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'tabla.csv';
      link.click();
    } catch (error) {
      console.error('Error al descargar todos los datos:', error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  // Función para actualizar la tabla y esperar 5 segundos
  const handleFetchClick = () => {
    fetchData(currentPage, pageSize);
    setIsFetchDisabled(true);
    setTimeout(() => {
      setIsFetchDisabled(false);
    }, 5000);
  };

  return (
    <>
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '10px',
        marginBottom: '20px',
        width: '100%',
        paddingRight: '5%'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <BigButton
            onClick={downloadTable}
            disabled={dataSource.length === 0 || loading || isDownloading}
            onMouseUp={(e) => e.currentTarget.blur()}
          >
            {isDownloading ? 'Descargando...' : 'Descargar tabla'}
          </BigButton>
          {isDownloading &&
            <div style={{
              marginLeft: '20px'
            }}
            >
              <Spin />
              <Progress
                percent={downloadProgress}
                size="small"
                style={{ marginLeft: '20px', width: '15vh' }}
              />
            </div>
          }
        </div>
        <div
          style={{
            paddingRight: '4.5%',
            position: 'relative',
            top: '15px'
          }}
        >
          <SmallIconButton
            onClick={handleFetchClick}
            disabled={loading || isDownloading || isFetchDisabled}
            icon={<SyncOutlined style={{ fontSize: '14px' }} />}
          >
          </SmallIconButton>
        </div>
      </div>
      <div
        style={{
          height: 'calc(100vh - 250px)',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <StyledTable
          dataSource={dataSource}
          columns={columns}
          pagination={{
            current: currentPage + 1,
            pageSize: pageSize,
            total: totalElements,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, size) => {
              if (pageSize !== size) {
                setCurrentPage(0);
                setPageSize(size);
              } else {
                setCurrentPage(page - 1);
              }
              fetchData(page - 1, size);
            },
          }}
          style={{
            margin: '0 auto',
          }}
          tableLayout="fixed"
        />
      </div>
    </>
  );
};

export default ObjectivesAdminCreationContent;

// ESTILOS
// Buttons
const BigButton = styled(AntButton)`
  background: #006600;
  font-size: 20px;
  font-weight: bold;
  line-height: 1.5;
  color: white;
  border: none;
  padding: 0px 60px;
  height: 40px; 
  margin-left: 30px;

  &:hover {
    background: #008000;
    color: white;
    border: none;
  }

  &:disabled {
    cursor: not-allowed;
    border: 1px solid;
    font-weight: normal;
  }

`;

const SmallIconButton = styled(AntButton)`
width: 32px !important;
height: 32px !important;
min-width: 24px !important;
padding: 0;
display: flex;
align-items: center;
justify-content: center;
font-size: 16px;

&:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
`;

// Table
const StyledTable = styled(Table)(({ cursor }) => ({
  cursor: cursor ? 'pointer' : 'initial',
  width: '80%',
  margin: '0 auto',
  '& .ant-table-container': {
    boxShadow: '4px 4px 5px 0px rgba(150,150,150,1)',
  },
  '& .ant-table-thead > tr > th': {
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 600,
    color: '#205527',
    borderRight: '1px solid #ccc',
  },
  '& .ant-table-thead > tr > th:last-child': {
    borderRight: 'none',
  },
  '& .ant-table-small > .ant-table-content > .ant-table-body': {
    margin: '0px',
    fontSize: '13px',
  },
  '& .ant-table-tbody > tr > td': {
    textAlign: 'center',
    background: '#F3FAF3',
    borderTop: '1px solid darkGray',
    borderBottom: 'none',
    borderRight: '1px solid #ccc',
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  },
  '& .ant-table-tbody > tr > td:last-child': {
    borderRight: 'none',
  },
  '& th.row-l-header.ant-table-align-center': {
    width: '130px !important',
  },
  '& td.row-l-body': {
    width: '130px !important',
  },
  '& th.row-s-header.ant-table-align-center': {
    width: '100px !important',
  },
  '& td.row-s-body': {
    width: '100px !important',
  },
  '& th.row-xs-header.ant-table-align-center': {
    width: '70px !important',
  },
  '& td.row-xs-body': {
    width: '70px !important',
  },
  '& .ant-table-row-cell-ellipsis': {
    width: '130px !Important',
  },
}));
